// « Pack scanner » hors ligne : modèles ONNX + index des cartes + moteur
// onnxruntime (.wasm), téléchargés depuis /scan/files et rangés dans
// Directory.Data/scanner/ (survit aux mises à jour OTA, comme le reste du
// cache hors ligne). Réservé à l'appli : sur le site, la reconnaissance passe
// toujours par le serveur.
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import { fetchScanManifest, scanFileUrl } from "../api";
import { LocalEngine } from "./engine";

const DIR = Directory.Data;
const ROOT = "scanner";
const LOCAL_MANIFEST = `${ROOT}/manifest.json`;
const CHUNK = 3 * 1024 * 1024; // écriture par morceaux : jamais 90 Mo d'un coup sur le pont natif

export const packAvailable = () => Capacitor.isNativePlatform();

export function packFiles(m) {
  return [m.runtime.file, ...Object.values(m.models).map((x) => x.file), ...Object.values(m.files)];
}

function fileSizes(m) {
  const sizes = { [m.runtime.file]: m.runtime.size };
  Object.values(m.models).forEach((x) => { sizes[x.file] = x.size; });
  Object.entries(m.files).forEach(([k, f]) => { sizes[f] = m.sizes?.[k] || 0; });
  return sizes;
}

export const packSize = (m) => Object.values(fileSizes(m)).reduce((a, b) => a + b, 0);

export async function localManifest() {
  try {
    const r = await Filesystem.readFile({ path: LOCAL_MANIFEST, directory: DIR, encoding: Encoding.UTF8 });
    return JSON.parse(r.data);
  } catch (e) {
    return null;
  }
}

async function exists(name) {
  try { await Filesystem.stat({ path: `${ROOT}/${name}`, directory: DIR }); return true; } catch (e) { return false; }
}

function toBase64(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

async function downloadOne(name, onBytes) {
  const path = `${ROOT}/${name}.part`;
  const resp = await fetch(scanFileUrl(name));
  if (!resp.ok) throw new Error(`Téléchargement impossible (${resp.status}) : ${name}`);
  const reader = resp.body.getReader();
  let pending = [], pendingLen = 0, first = true;
  const flush = async () => {
    if (!pendingLen) return;
    const buf = new Uint8Array(pendingLen); let o = 0;
    for (const p of pending) { buf.set(p, o); o += p.length; }
    pending = []; pendingLen = 0;
    const data = toBase64(buf);
    if (first) { await Filesystem.writeFile({ path, data, directory: DIR, recursive: true }); first = false; }
    else await Filesystem.appendFile({ path, data, directory: DIR });
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    pending.push(value); pendingLen += value.length; onBytes(value.length);
    if (pendingLen >= CHUNK) await flush();
  }
  await flush();
  await Filesystem.rename({ from: path, to: `${ROOT}/${name}`, directory: DIR, toDirectory: DIR });
}

/** Télécharge (ou met à jour) le pack. onProgress(fraction 0..1). Ne
 * retélécharge que les fichiers qui ont changé (noms versionnés). */
export async function downloadPack(onProgress) {
  const m = await fetchScanManifest();
  const files = packFiles(m), sizes = fileSizes(m), total = packSize(m) || 1;
  let done = 0;
  for (const f of files) {
    if (await exists(f)) { done += sizes[f] || 0; continue; }
    await downloadOne(f, (n) => { done += n; onProgress?.(Math.min(1, done / total)); });
  }
  await Filesystem.writeFile({ path: LOCAL_MANIFEST, data: JSON.stringify(m), directory: DIR, encoding: Encoding.UTF8, recursive: true });
  await pruneOldFiles(new Set(files));
  engineCache = null;
  onProgress?.(1);
  return m;
}

async function pruneOldFiles(keep) {
  try {
    const { files } = await Filesystem.readdir({ path: ROOT, directory: DIR });
    for (const f of files) {
      const name = f.name || f;
      if (name !== "manifest.json" && !keep.has(name)) {
        await Filesystem.deleteFile({ path: `${ROOT}/${name}`, directory: DIR }).catch(() => {});
      }
    }
  } catch (e) { /* dossier absent */ }
}

export async function deletePack() {
  engineCache = null;
  await Filesystem.rmdir({ path: ROOT, directory: DIR, recursive: true }).catch(() => {});
}

/** true si une version plus récente du pack existe sur le serveur. */
export async function packUpdateAvailable(local) {
  try {
    const m = await fetchScanManifest();
    return !local || m.version !== local.version || m.runtime.file !== local.runtime.file ||
      Object.keys(m.models).some((k) => m.models[k].file !== local.models?.[k]?.file);
  } catch (e) {
    return false;
  }
}

let engineCache = null;

/** Moteur prêt à l'emploi sur le pack local, ou null si pas de pack. */
export async function getLocalEngine(onStep) {
  if (engineCache) return engineCache;
  const m = await localManifest();
  if (!m) return null;
  const { uri } = await Filesystem.getUri({ path: ROOT, directory: DIR });
  const base = Capacitor.convertFileSrc(uri);
  const engine = new LocalEngine(m, (name) => `${base}/${name}`);
  await engine.init(onStep);
  engineCache = engine;
  return engine;
}

/** Moteur embarqué alimenté directement par le serveur (essais sur PC). */
export async function getRemoteFilesEngine(onStep) {
  if (engineCache) return engineCache;
  const m = await fetchScanManifest();
  const engine = new LocalEngine(m, scanFileUrl);
  await engine.init(onStep);
  engineCache = engine;
  return engine;
}
